import { useLocation, Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { useRouteNameMap, useRouteParentMap } from '@/hooks/usePageTitle';
import { useBreadcrumbContext } from '@/contexts/BreadcrumbContext';
import { getCachedGroupName } from '@/lib/groupNameCache';

interface BreadcrumbItem {
  label: string;
  path: string;
}

// Static fallback names (used when dynamic map has no entry)
const staticRouteNames: Record<string, string> = {
  '': 'Dashboard',
  'quick-order': 'Quick Order',
  'units': 'VIN/Unit Search',
  'unit-options': 'Options',
  'unit-parts': 'Parts',
  'orders': 'Orders',
  'cart': 'Cart',
  'checkout': 'Checkout',
  'saved-carts': 'Saved Carts',
  'dealer-claims': 'Claims and Pre-Auths',
  'dealer-spiffs': "Dealer SPIFF's",
  'dealer-pre-authorizations': 'Pre-Authorizations',
  'service-campaigns': 'Service Campaigns',
  'my-spiffs': "My Claimed SPIFF's",
  'salesperson-spiffs': "Sales Person SPIFF's",
  'parts': 'Shop RV Parts',
  'product-groups': 'Product Groups',
  'my-favorites': 'My Favorites',
  'addresses': 'Addresses',
  'contact-info': 'Contact Information',
  'message-board': 'Message Board',
  'digital-assets': 'Digital Assets',
  'case-management': 'Case Management',
  'your-tickets': 'Your Tickets',
  'warranty-registration': 'Unit/Warranty Registration',
  'unit-registration': 'Register Unit',
  'confirmation': 'Registration Complete',
  'error': 'Registration Error',
  'rga-list': "RGA's",
  'create-rga': 'Create RGA',
  'new-claim': 'New Claim',
  'new-pre-authorization': 'New Pre-Authorization',
  'unknown-part-request': 'Unknown Part Request',
  'view-dealer-invoices': 'Dealer Invoices',
  'labor-operations': 'Labor Operations Library',
  'inventory-transfers': 'Inventory Transfers',
  'search-results': 'Search Results',
};

export function Breadcrumbs() {
  const location = useLocation();
  const dynamicRouteNames = useRouteNameMap();
  const routeParentMap = useRouteParentMap();
  const { customBreadcrumbs, onBreadcrumbClick, lastSegmentLabel } = useBreadcrumbContext();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const navRef = useRef<HTMLElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Auto-scroll to right and detect overflow
  useEffect(() => {
    const nav = navRef.current;
    if (nav) {
      nav.scrollLeft = nav.scrollWidth;
      setHasOverflow(nav.scrollWidth > nav.clientWidth);
    }
  }, [breadcrumbs, customBreadcrumbs]);

  // Build a segment-level lookup: merge static with dynamic (route -> name keyed by segment)
  const resolveSegmentName = (segment: string): string | undefined => {
    const dynamicName = dynamicRouteNames['/' + segment];
    if (dynamicName) return dynamicName;
    return staticRouteNames[segment];
  };

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const searchParams = new URLSearchParams(location.search);
    const items: BreadcrumbItem[] = [
      { label: 'Home', path: '/' }
    ];

    // Special handling for /parts/browse/:groupId and /parts/products/:groupId
    const isPartsBrowse = pathSegments[0] === 'parts' && pathSegments[1] === 'browse' && pathSegments[2];
    const isPartsProducts = pathSegments[0] === 'parts' && pathSegments[1] === 'products' && pathSegments[2];
    const isProductDetail = pathSegments[0] === 'products' && pathSegments[1];

    // Special handling for unit-registration (child of warranty-registration)
    const isUnitRegistration = pathSegments[0] === 'unit-registration';

    // Special handling for unit subpages (unit-options, unit-parts)
    const vinSubpages = ['unit-options', 'unit-parts'];
    const isVinSubpage = pathSegments.some(seg => vinSubpages.includes(seg));
    const vinParam = searchParams.get('vin');

    if (isPartsBrowse || isPartsProducts || isProductDetail) {
      items.push({ label: 'Shop RV Parts', path: '/parts' });
    } else if (isUnitRegistration) {
      items.push({ label: 'Unit/Warranty Registration', path: '/warranty-registration' });
      items.push({ label: 'Register Unit', path: '/unit-registration' });
      if (pathSegments.length > 1) {
        items.push({ label: resolveSegmentName(pathSegments[1]) || pathSegments[1], path: location.pathname });
      }
    } else if (isVinSubpage && vinParam) {
      items.push({ label: 'Units', path: '/units' });
      items.push({ label: vinParam, path: `/units/${vinParam}` });
      
      pathSegments.forEach(segment => {
        if (vinSubpages.includes(segment)) {
          items.push({
            label: resolveSegmentName(segment) || segment,
            path: `/${segment}?vin=${vinParam}`
          });
        }
      });
    } else {
      let currentPath = '';
      const segmentsToProcess = pathSegments;
      
      segmentsToProcess.forEach((segment, index) => {
        currentPath += `/${segment}`;
        
        let label = resolveSegmentName(segment) || segment;
        
        if (segment.match(/^[0-9a-f-]{36}$/i) || segment.match(/^\d+$/)) {
          if (index > 0) {
            const prevSegment = segmentsToProcess[index - 1];
            if (prevSegment === 'units') {
              label = segment;
            } else if (prevSegment === 'products') {
              label = `Product ${segment}`;
            } else if (prevSegment === 'digital-assets') {
              label = `Asset ${segment}`;
            } else if (prevSegment === 'service-campaigns') {
              label = `Campaign ${segment}`;
            } else {
              label = 'Details';
            }
          }
        }

        // Insert parent group crumb before the first segment
        if (index === 0) {
          const parentName = routeParentMap['/' + segment];
          if (parentName) {
            items.push({ label: parentName, path: '' });
          }
        }

        items.push({
          label,
          path: currentPath
        });
      });
    }

    setBreadcrumbs(items);
  }, [location.pathname, location.search, dynamicRouteNames]);

  // Don't show breadcrumbs on home page
  if (location.pathname === '/') {
    return null;
  }

  const hasCustomBreadcrumbs = customBreadcrumbs.length > 0;
  const isPartsSubpath = (location.pathname.startsWith('/parts/') || location.pathname.startsWith('/products/')) && hasCustomBreadcrumbs;
  const baseBreadcrumbs = isPartsSubpath
    ? breadcrumbs.filter(item => item.path === '/' || item.path === '/parts')
    : breadcrumbs;

  // Apply lastSegmentLabel override to the final breadcrumb
  const displayBreadcrumbs = lastSegmentLabel && !hasCustomBreadcrumbs && baseBreadcrumbs.length > 1
    ? baseBreadcrumbs.map((item, i) =>
        i === baseBreadcrumbs.length - 1 ? { ...item, label: lastSegmentLabel } : item
      )
    : baseBreadcrumbs;

  return (
    <div className="bg-muted/30 border-b border-border/20 px-6">
      <div className="max-w-[1400px] mx-auto py-2 relative overflow-hidden">
        {hasOverflow && (
          <div className="absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-r from-background to-transparent" />
        )}
        <nav ref={navRef} className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm overflow-x-auto scrollbar-hide flex-nowrap whitespace-nowrap">
          {displayBreadcrumbs.map((item, index) => {
            const isLastRouteItem = index === displayBreadcrumbs.length - 1;
            const isCurrentPage = isLastRouteItem && !hasCustomBreadcrumbs;

            return (
              <div key={item.path} className="flex items-center gap-2">
                {index === 0 ? (
                  <Link
                    to={item.path}
                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Home className="h-3.5 w-3.5" />
                    <span>{item.label}</span>
                  </Link>
                ) : (
                  <>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                    {isCurrentPage ? (
                      <span className="font-medium text-foreground">{item.label}</span>
                    ) : item.path === '' ? (
                      <span className="text-muted-foreground">{item.label}</span>
                    ) : (
                      <Link
                        to={item.path}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {item.label}
                      </Link>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Custom breadcrumbs injected by the page */}
          {customBreadcrumbs.map((item, index) => {
            const isLast = index === customBreadcrumbs.length - 1;
            return (
              <div key={`custom-${index}`} className="flex items-center gap-2">
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                {isLast ? (
                  <span className="font-medium text-foreground">{item.label}</span>
                ) : (
                  <button
                    onClick={() => onBreadcrumbClick(index)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </button>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
